// useTrialStatus.js
// Custom hook to manage trial period, expiry, and read-only mode
// Works in conjunction with useSubscriptionFeatures for full access control

import { useMemo } from 'react';

/**
 * Hook to check trial status and determine if user has active access
 * @param {Object} user - User object from Firebase with subscription details
 * @returns {Object} Trial status and access control flags
 */
export const useTrialStatus = (user) => {
  const trialStatus = useMemo(() => {
    if (!user) {
      return {
        isInTrial: false,
        isTrialExpired: false,
        isActive: false,
        daysRemaining: 0,
        trialEndsAt: null,
        canEdit: false,
        showTrialBanner: false,
        subscriptionStatus: 'unknown'
      };
    }

    const now = new Date();
    const createdAt = user.createdAt?.toDate ? user.createdAt.toDate() : new Date(user.createdAt);
    const trialEndsAt = user.trialEndsAt?.toDate ? user.trialEndsAt.toDate() : new Date(user.trialEndsAt);
    
    // Calculate days remaining in trial
    const msRemaining = trialEndsAt - now;
    const daysRemaining = Math.ceil(msRemaining / (1000 * 60 * 60 * 24));

    // Determine subscription status
    const subscriptionStatus = user.subscriptionStatus || 'trial';
    
    // User states:
    // 1. In trial (trial status, not expired)
    // 2. Trial expired (trial status, expired, no subscription)
    // 3. Active subscriber (has subscription tier)
    
    const isInTrial = subscriptionStatus === 'trial' && daysRemaining > 0;
    const isTrialExpired = subscriptionStatus === 'expired' || (subscriptionStatus === 'trial' && daysRemaining <= 0);
    const isActive = subscriptionStatus === 'active';

    // Can edit if: (In trial) OR (Active subscription)
    const canEdit = isInTrial || isActive;

    // Show trial banner if in trial with 3 or fewer days remaining
    const showTrialBanner = isInTrial;

    return {
      isInTrial,
      isTrialExpired,
      isActive,
      daysRemaining: Math.max(0, daysRemaining),
      trialEndsAt,
      canEdit,
      showTrialBanner,
      subscriptionStatus,
      createdAt
    };
  }, [user]);

  return trialStatus;
};

/**
 * Helper to get trial banner message based on days remaining
 */
export const getTrialBannerMessage = (daysRemaining) => {
  if (daysRemaining === 0) {
    return '⏰ Your trial ends today! Choose a plan to continue.';
  } else if (daysRemaining === 1) {
    return '⏰ Your trial ends tomorrow! Choose a plan to continue.';
  } else {
    return `⏰ ${daysRemaining} days left in your trial. Choose a plan to continue.`;
  }
};

/**
 * Helper to calculate trial end date from creation date
 */
export const calculateTrialEndDate = (createdAt, trialDays = 7) => {
  const created = createdAt?.toDate ? createdAt.toDate() : new Date(createdAt);
  const endDate = new Date(created);
  endDate.setDate(endDate.getDate() + trialDays);
  return endDate;
};

/**
 * Combined access control check
 * Use this before any create/edit/delete operation
 */
export const useAccessControl = (user, subscriptionFeatures) => {
  const trialStatus = useTrialStatus(user);
  
  return {
    // Trial-based access (read-only mode check)
    canEdit: trialStatus.canEdit,
    isReadOnly: trialStatus.isTrialExpired,
    
    // Feature-based access (tier restrictions)
    canCreateRecurring: trialStatus.canEdit && subscriptionFeatures.canCreateRecurringAllocations,
    canUseShareLinks: trialStatus.canEdit && subscriptionFeatures.canCreateShareLinks,
    canImport: trialStatus.canEdit && subscriptionFeatures.canImportAllocations,
    canUseFacilitySharing: trialStatus.canEdit && subscriptionFeatures.canUseFacilitySharing,
    canViewReports: subscriptionFeatures.canViewReports,
    canPrintReports: trialStatus.canEdit && subscriptionFeatures.canPrintReports,
    canViewChangelog: subscriptionFeatures.canViewChangelog,
    canViewWeatherAlerts: subscriptionFeatures.canViewWeatherAlerts,
    
    // Trial info
    ...trialStatus,
    
    // Combined tier info
    tier: subscriptionFeatures.tier
  };
};

export default useTrialStatus;
